import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import AssignmentOutlined from '@mui/icons-material/AssignmentOutlined';
import { useHistory } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import {
  isReportResponse,
  ManagerReport,
  selectManagerReports,
} from 'pages/Reporting/redux';
import { selectUserEmail } from 'pages/app/redux';
import { ERP_PATHS } from 'pages/Layout/erpModulePaths';
import { usePath } from 'pages/hooks';
import LayoutAlertPresence from './LayoutAlertPresence';
import { logButtonClick } from 'utils/analytics';
import {
  fetchReportRequests,
  ReportRequestListItem,
} from 'pages/Forms/formsReportsApi';
import * as S from './ManagerReportsAlert.styled';

const DISMISS_STORAGE_KEY = 'manager_reports_alert_dismissed_v1';
const DISMISS_MS = 60 * 60 * 1000; // 1 hour

type ManagerReportsAlertDismissState = {
  until: number,
  /** Inbox ids present when dismissed — any newer id brings the alert back. */
  knownIds: string[],
};

const emptyDismissState = (): ManagerReportsAlertDismissState => ({
  until: 0,
  knownIds: [],
});

const readDismissState = (): ManagerReportsAlertDismissState => {
  try {
    const raw = localStorage.getItem(DISMISS_STORAGE_KEY);
    if (!raw) return emptyDismissState();
    const parsed = JSON.parse(raw);
    const knownIds = Array.isArray(parsed?.knownIds)
      ? parsed.knownIds.map((id: unknown) => String(id)).filter(Boolean)
      : [];
    return {
      until: Number(parsed?.until) || 0,
      knownIds,
    };
  } catch {
    return emptyDismissState();
  }
};

const writeDismissState = (state: ManagerReportsAlertDismissState) => {
  try {
    localStorage.setItem(DISMISS_STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore quota / private mode */
  }
};

export const filterManagerReportsForUser = (
  reports: ManagerReport[] | null | undefined,
  userEmail: string | null | undefined,
): ManagerReport[] => {
  const email = String(userEmail || '').trim().toLowerCase();
  if (!email || !reports?.length) return [];
  return reports.filter((r) => {
    if (String(r.toEmail || '').toLowerCase() !== email) return false;
    if (r.isOutgoing) return false;
    if (r.reviewedAt) return false;
    return true;
  });
};

export const filterIncomingReportRequests = (
  requests: ReportRequestListItem[],
): ReportRequestListItem[] => (
  requests.filter((row) => row.isIncoming && String(row.status || '').toLowerCase() === 'open')
);

type AlertInboxItem =
  | { kind: 'report', id: string, createdAt: string, report: ManagerReport }
  | { kind: 'report-request', id: string, createdAt: string, request: ReportRequestListItem };

const alertItemKey = (item: AlertInboxItem): string => (
  item.kind === 'report-request' ? `req:${item.id}` : item.id
);

/** True while the current inbox is snoozed (no new ids since dismiss). */
export const isManagerReportsAlertSnoozed = (
  itemKeys: string[],
  dismissState: ManagerReportsAlertDismissState = readDismissState(),
): boolean => {
  if (!itemKeys.length) return false;
  if (Date.now() >= dismissState.until) return false;
  const known = new Set(dismissState.knownIds);
  return itemKeys.every((id) => known.has(id));
};

/**
 * Inbox strip for reports + report requests — higher priority than storage (MB) alerts.
 */
export const isManagerReportsAlertActive = (
  reports: ManagerReport[] | null | undefined,
  isHomePath: boolean,
  userEmail?: string | null,
  incomingReportRequestCount = 0,
): boolean => {
  if (isHomePath) return false;
  const mine = filterManagerReportsForUser(reports, userEmail);
  if (!mine.length && !incomingReportRequestCount) return false;
  if (incomingReportRequestCount && !mine.length) return true;
  const ids = mine.map((r) => String(r.id));
  return !isManagerReportsAlertSnoozed(ids);
};

const ManagerReportsAlert = () => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const reports = useAppSelector(selectManagerReports);
  const userEmail = useAppSelector(selectUserEmail);
  const { isHomePath } = usePath();
  const [dismissState, setDismissState] = useState(readDismissState);
  const [reportRequests, setReportRequests] = useState<ReportRequestListItem[]>([]);

  const refreshReportRequests = useCallback(async () => {
    const rows = await fetchReportRequests(dispatch);
    setReportRequests(filterIncomingReportRequests(rows));
  }, [dispatch]);

  useEffect(() => {
    refreshReportRequests();
  }, [refreshReportRequests]);

  useEffect(() => {
    const onRefresh = () => { refreshReportRequests(); };
    window.addEventListener('forms_inbox_refresh', onRefresh);
    window.addEventListener('forms_inbox_counts_refresh', onRefresh);
    return () => {
      window.removeEventListener('forms_inbox_refresh', onRefresh);
      window.removeEventListener('forms_inbox_counts_refresh', onRefresh);
    };
  }, [refreshReportRequests]);

  const myReports = useMemo(
    () => filterManagerReportsForUser(reports, userEmail),
    [reports, userEmail],
  );

  const inboxItems = useMemo((): AlertInboxItem[] => {
    const items: AlertInboxItem[] = [
      ...myReports.map((report) => ({
        kind: 'report' as const,
        id: String(report.id),
        createdAt: String(report.createdAt || ''),
        report,
      })),
      ...reportRequests.map((request) => ({
        kind: 'report-request' as const,
        id: String(request.id),
        createdAt: String(request.createdAt || ''),
        request,
      })),
    ];
    return items.sort((a, b) => (
      new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
    ));
  }, [myReports, reportRequests]);

  const latest = inboxItems[0];
  const itemKeys = useMemo(() => inboxItems.map(alertItemKey), [inboxItems]);
  const snoozed = isManagerReportsAlertSnoozed(itemKeys, dismissState);
  const reportCount = myReports.length;
  const requestCount = reportRequests.length;
  const count = reportCount + requestCount;
  const active = !isHomePath && count > 0 && !snoozed;

  const latestReport = useMemo(
    () => inboxItems.find((item) => item.kind === 'report') || null,
    [inboxItems],
  );
  const latestRequest = useMemo(
    () => inboxItems.find((item) => item.kind === 'report-request') || null,
    [inboxItems],
  );

  const onDismiss = () => {
    const next: ManagerReportsAlertDismissState = {
      until: Date.now() + DISMISS_MS,
      knownIds: itemKeys,
    };
    writeDismissState(next);
    setDismissState(next);
    logButtonClick('dismiss_manager_reports_alert');
  };

  const openReport = (item: Extract<AlertInboxItem, { kind: 'report' }>) => {
    const isResponse = isReportResponse(item.report);
    logButtonClick(
      isResponse ? 'view_manager_report_response_alert' : 'view_manager_report_alert',
    );
    history.push(ERP_PATHS.formsReport(String(item.report.id)));
  };

  const openRequest = (item: Extract<AlertInboxItem, { kind: 'report-request' }>) => {
    logButtonClick('view_report_request_alert');
    history.push(ERP_PATHS.formsReportRequest(item.id));
  };

  const latestText = (() => {
    if (!latest) return 'Open to view';
    if (latest.kind === 'report-request') {
      return latest.request.subject || latest.request.message || 'Open to view';
    }
    const report = latest.report;
    const prefix = isReportResponse(report) ? 'Response — ' : '';
    return `${prefix}${report.subject || report.preview || report.description || 'Open to view'}`;
  })();

  const latestIsRequest = latest?.kind === 'report-request';
  const latestIsResponse = latest?.kind === 'report' && isReportResponse(latest.report);

  const open = active && !!latest;

  return (
    <LayoutAlertPresence open={open}>
      {latest ? (
        <S.Container $belowOverlays role="status">
          <S.Categories>
            {reportCount > 0 ? (
              <S.CategoryBtn
                type="button"
                $accent={S.reportCategoryAccent}
                aria-label={`${reportCount} staff report${reportCount === 1 ? '' : 's'}`}
                onClick={() => {
                  if (latestReport?.kind === 'report') openReport(latestReport);
                }}
              >
                <S.CategoryIcon $accent={S.reportCategoryAccent} aria-hidden>
                  <DescriptionOutlined />
                </S.CategoryIcon>
                <S.CategoryLabel>Reports</S.CategoryLabel>
                <S.CountBadge $accent={S.reportCategoryAccent}>{reportCount}</S.CountBadge>
              </S.CategoryBtn>
            ) : null}
            {requestCount > 0 ? (
              <S.CategoryBtn
                type="button"
                $accent={S.requestCategoryAccent}
                aria-label={`${requestCount} report request${requestCount === 1 ? '' : 's'}`}
                onClick={() => {
                  if (latestRequest?.kind === 'report-request') openRequest(latestRequest);
                }}
              >
                <S.CategoryIcon $accent={S.requestCategoryAccent} aria-hidden>
                  <AssignmentOutlined />
                </S.CategoryIcon>
                <S.CategoryLabel>Report requests</S.CategoryLabel>
                <S.CountBadge $accent={S.requestCategoryAccent}>{requestCount}</S.CountBadge>
              </S.CategoryBtn>
            ) : null}
          </S.Categories>

          <S.Body>
            <S.LatestLabel>Latest</S.LatestLabel>
            <S.LatestText title={latestText}>{latestText}</S.LatestText>
          </S.Body>

          <S.Actions>
            <S.SecondaryBtn type="button" onClick={onDismiss}>
              Dismiss
            </S.SecondaryBtn>
            <S.PrimaryBtn
              type="button"
              onClick={() => {
                if (latest.kind === 'report-request') {
                  openRequest(latest);
                  return;
                }
                openReport(latest);
              }}
            >
              {latestIsRequest
                ? 'View request'
                : (latestIsResponse ? 'View response' : 'View report')}
            </S.PrimaryBtn>
          </S.Actions>
        </S.Container>
      ) : null}
    </LayoutAlertPresence>
  );
};

export default ManagerReportsAlert;
